import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { sendButtonPress } from "../../../../axios/buttons";
import { format } from "date-fns";
import { Message, MessageState, MessegeButtons } from "../../../../types";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { useAppStore } from "../../../../stores/appStore";
import { useChatStore } from "../../../../stores/chatStore";
import { AudioButton } from "../audio-button/audio-button";
import { resolveTranscriptData } from "../../../../utils/transcription";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../../utils/internetConnection";

import {
  containerStyle,
  embeddedContainerStyle,
  messageTextStyle,
  gridStyle,
  getButtonStyles,
  timeStyle,
  messageTextStyleSuggestion,
  timeStyleSystem,
  buttonStylesSuggestion,
  buttonContainerSuggestion,
  mobileWarningButtonStyle,
} from "./styles";

interface MessageButtonsProps {
  message: Message;
  buttons: MessegeButtons[][];
  messageId: string;
  userId: string;
  tabId?: string;
  state?: MessageState;
  currentReaction?: string;
  isSaved?: boolean;
  isTags?: string[];
  markedIds: Record<string, string>;
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  // When true, hide ReactionAndFavorite and timestamp (used inside TabbedMessage)
  embedded?: boolean;
  isSuggestionTab?: boolean;
  // Called when user selects an answer (after successful sendButtonPress)
  onQuizAnswered?: () => void;
}

const MessageButtons: React.FC<MessageButtonsProps> = ({
  message,
  buttons: initialButtons,
  messageId,
  userId,
  tabId,
  state,
  currentReaction,
  isSaved,
  isTags,
  markedIds,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  embedded = false,
  isSuggestionTab = false,
  onQuizAnswered,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const [buttons, setButtons] = useState<MessegeButtons[][]>(initialButtons);
  const [showLottie, setShowLottie] = useState(false);
  const [hasAudioPlayed, setHasAudioPlayed] = useState(false);
  const [selectedChipValues, setSelectedChipValues] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(() => {
    const isSuggestion =
      isSuggestionTab || message.message_type === "suggestion";
    if (isSuggestion) return false;
    return initialButtons.some((row) => row.some((btn) => btn.isChosen));
  });
  const submitInFlightRef = useRef(false);

  const { isBrowserLocked, setIsBrowserLocked, isOnline } = useAppStore();
  const { setMessages, setTargetMessageId } = useChatStore();

  const isSuggestionPress =
    isSuggestionTab || message.message_type === "suggestion";

  const isOffline = !isOnline || isBrowserOffline();

  const isButtonsLocked =
    state === "disable" ||
    isBrowserLocked ||
    isSubmitting ||
    (!isSuggestionPress && hasAnswered);

  const isButtonDisabled = (button: MessegeButtons) =>
    isSuggestionPress
      ? !!button.isChosen || state === "disable" || isBrowserLocked
      : isButtonsLocked;

  const scrollToUpcomingAnswer = () => {
    setTargetMessageId(null);
    window.dispatchEvent(new CustomEvent("messageMenuActionStarted"));
  };
  const resolvedTranscript = resolveTranscriptData({
    transcription: message.transcription ?? message.audio?.transcription,
    transcriptionTimed: message.transcription_timed ?? message.audio?.transcription_timed,
  });

  useEffect(() => {
    setButtons(initialButtons);
    if (!isSuggestionPress) {
      setHasAnswered(
        initialButtons.some((row) => row.some((btn) => btn.isChosen)),
      );
    }
  }, [initialButtons, isSuggestionPress]);

  useEffect(() => {
    const selectedFromServer = initialButtons
      .flat()
      .filter((btn) => btn.isChosen)
      .map((btn) => btn.data);
    setSelectedChipValues(selectedFromServer);
  }, [initialButtons]);

  // Check if this is a mobile warning message
  const isMobileWarning =
    buttons.some((row) =>
      row.some(
        (btn) =>
          btn.data === "continue_mobile_warning" ||
          btn.data === "exit_mobile_warning"
      )
    ) ||
    message.text?.includes("Role play may not work well on mobile devices");

  const handleMobileWarningButton = (buttonData: string) => {
    if (buttonData === "continue_mobile_warning") {
      window.dispatchEvent(new CustomEvent("openRealtimeMenu"));
      setMessages((prev) => prev.filter((msg) => msg.message_id !== messageId));
    } else if (buttonData === "exit_mobile_warning") {
      setMessages((prev) => prev.filter((msg) => msg.message_id !== messageId));
    }
  };

  const handleAudioStateChange = (playing: boolean) => {
    if (!playing) {
      setHasAudioPlayed(true);
    }
  };

  const handleButtonClick = async (
    button: MessegeButtons,
    rowIndex: number,
    buttonIndex: number
  ) => {
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }

    if (isSuggestionPress) {
      if (isButtonDisabled(button)) return;
    } else if (isButtonsLocked || submitInFlightRef.current) {
      return;
    }

    if (
      button.data === "continue_mobile_warning" ||
      button.data === "exit_mobile_warning"
    ) {
      handleMobileWarningButton(button.data);
      return;
    }

    if (!isSuggestionPress) {
      submitInFlightRef.current = true;
      setHasAnswered(true);
      setIsBrowserLocked(true);
    }
    setIsSubmitting(true);

    if (isSuggestionPress) {
      scrollToUpcomingAnswer();
    }

    setButtons((prevButtons) => {
      const newButtons = [...prevButtons];
      newButtons[rowIndex] = [...newButtons[rowIndex]];
      newButtons[rowIndex][buttonIndex] = {
        ...button,
        isChosen: true,
      };
      return newButtons;
    });

    const buttonData = {
      user_id: userId,
      message_id: messageId,
      data: [button.data],
      ...(tabId ? { tab_id: tabId } : {}),
    };

    try {
      const response = await sendButtonPress(buttonData);
      const updatedButtons = response?.buttons ?? response?.data?.buttons;
      if (updatedButtons) {
        setButtons(updatedButtons);
      }
      if (!isSuggestionPress) {
        onQuizAnswered?.();
      }
      if (!showLottie) {
        setShowLottie(true);
      }
    } catch (error) {
      console.error("Error sending button data:", error);
      if (isSuggestionPress) {
        setButtons((prevButtons) => {
          const newButtons = [...prevButtons];
          newButtons[rowIndex] = [...newButtons[rowIndex]];
          newButtons[rowIndex][buttonIndex] = {
            ...button,
            isChosen: false,
          };
          return newButtons;
        });
      } else {
        submitInFlightRef.current = false;
        setHasAnswered(false);
        setButtons(initialButtons);
        setIsBrowserLocked(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const infoScaleSx =
    message.message_type === "info"
      ? { xs: "1.2em", sm: "1.35em", md: "1.5em" }
      : undefined;
  const isChipsMessage = (message.message_type as string) === "chips";
  const isMultichoice = Boolean(
    message.isMultiple ??
      (message as Message & { multichoice?: boolean }).multichoice
  );
  const titleFontSx = {
    xs: "0.9375rem",
    sm: "1.125rem",
    md: "1.1875rem",
  };
  const getResolvedButtonColor = (button: MessegeButtons) => {
    const baseColor = getButtonStyles.getButtonColor(
      theme,
      !!button.isChosen,
      !!button.isCorrect
    );

    if (theme.palette.mode === "light" && !button.isChosen && button.isCorrect) {
      return "#A7F3D0";
    }

    return baseColor;
  };
  const rootContainerSx = embedded
    ? embeddedContainerStyle(state === "disable")
    : containerStyle(state === "disable");
  const embeddedLightTextColor =
    embedded && theme.palette.mode === "light" ? "#000000" : undefined;
  const messageTextBoxSx = (muiTheme: any) => ({
    ...(typeof messageTextStyle === "function"
      ? messageTextStyle(muiTheme)
      : (messageTextStyle as any)),
    ...(embeddedLightTextColor ? { color: embeddedLightTextColor } : {}),
  });

  const submitChipSelection = async (selectedValues: string[]) => {
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }
    if (isButtonsLocked || submitInFlightRef.current || selectedValues.length === 0) {
      return;
    }

    submitInFlightRef.current = true;
    setHasAnswered(true);
    setIsSubmitting(true);
    setIsBrowserLocked(true);

    const buttonData = {
      user_id: userId,
      message_id: messageId,
      data: selectedValues,
      ...(tabId ? { tab_id: tabId } : {}),
    };

    const previousButtons = buttons;
    const selectedSet = new Set(selectedValues);
    setButtons((prevButtons) =>
      prevButtons.map((row) =>
        row.map((btn) => ({
          ...btn,
          isChosen: selectedSet.has(btn.data),
        }))
      )
    );

    try {
      const response = await sendButtonPress(buttonData);
      const updatedButtons = response?.buttons ?? response?.data?.buttons;
      if (updatedButtons) {
        setButtons(updatedButtons);
      }
      onQuizAnswered?.();
    } catch (error) {
      console.error("Error sending chips data:", error);
      submitInFlightRef.current = false;
      setHasAnswered(false);
      setButtons(previousButtons);
      setIsBrowserLocked(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleChipSelection = async (button: MessegeButtons) => {
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }
    if (isButtonsLocked) return;

    if (!isMultichoice) {
      setSelectedChipValues([button.data]);
      await submitChipSelection([button.data]);
      return;
    }

    setSelectedChipValues((prev) =>
      prev.includes(button.data)
        ? prev.filter((value) => value !== button.data)
        : [...prev, button.data]
    );
  };

  if (message.message_type === "suggestion") {
    return (
      <Box sx={buttonContainerSuggestion} position="relative">
        {message?.title && (
          <Box mb="12px" sx={messageTextStyleSuggestion}>
            <Typography variant="h6" sx={{ fontSize: titleFontSx }}>
              <CustomMarkdown children={message.title} />
            </Typography>
          </Box>
        )}
        {message?.text && (
          <Box mb="12px" sx={messageTextStyleSuggestion}>
            <CustomMarkdown children={message.text} />
          </Box>
        )}

        {buttons && buttons.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
            {buttons.map((row, rowIndex) => (
              <Box key={rowIndex} sx={{ display: "flex", gap: 1 }}>
                {row.map((button, buttonIndex) => {
                  const buttonText = String(
                    button.text || button.data || ""
                  ).toLowerCase();
                  const isOpenButton =
                    buttonText === "open" ||
                    buttonText === "start Roleplay" ||
                    buttonText.includes("start");

                  const audioUrl =
                    message.audio?.audio_url ||
                    (button as any)?.audioUrl ||
                    (button.data as any)?.audioUrl ||
                    (message as any)?.audioUrl ||
                    (message as any)?.data?.audioUrl;

                  if (isOpenButton && audioUrl) {
                    return (
                      <Box
                        key={`open-pair-sugg-${rowIndex}-${buttonIndex}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          alignItems: "stretch",
                          minWidth: "200px",
                        }}
                      >
                        <AudioButton
                          buttonText={message.audio?.button_text || "Audio"}
                          audioUrl={audioUrl}
                          onAudioStateChange={handleAudioStateChange}
                          disabled={isButtonDisabled(button)}
                          transcription={resolvedTranscript.transcription}
                          transcriptionTimed={resolvedTranscript.transcriptionTimed}
                        />

                        {hasAudioPlayed && (
                          <Button
                            onClick={() =>
                              handleButtonClick(button, rowIndex, buttonIndex)
                            }
                            variant="outlined"
                            disabled={isButtonDisabled(button)}
                            sx={buttonStylesSuggestion}
                          >
                            <CustomMarkdown children={button.text} />
                          </Button>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <Button
                      key={buttonIndex}
                      onClick={() =>
                        handleButtonClick(button, rowIndex, buttonIndex)
                      }
                      variant="outlined"
                      disabled={isButtonDisabled(button)}
                      sx={buttonStylesSuggestion}
                    >
                      <CustomMarkdown children={button.text} />
                    </Button>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
        {!embedded && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              <ReactionAndFavorite
                currentReaction={currentReaction}
                markedIds={markedIds}
                isSaved={isSaved}
                isTags={isTags}
                handleReactionClick={handleReactionClick}
                handleAddToFavorite={handleAddToFavorite}
                handleRemoveTag={handleRemoveTag}
                reactionBg={getButtonStyles.buttonColors(theme).bg}
              />
            </Box>
            {message.date && (
              <Typography variant="caption" sx={timeStyleSystem}>
              <span>{format(new Date(message.date), "dd/MM/yy")}</span>
              <span style={{ margin: "0 5px" }} />
              <span>{format(new Date(message.date), "HH:mm")}</span>
            </Typography>
          )}
          </Box>
        )}
      </Box>
    );
  }

  if (isChipsMessage) {
    const selectedValuesSet = new Set(selectedChipValues);
    return (
      <Box sx={rootContainerSx} position="relative">
        <Box sx={{ fontSize: infoScaleSx, width: "100%" }}>
          {message?.title && (
            <Box mb="12px" sx={messageTextStyle}>
              <Typography variant="h6" sx={{ fontSize: titleFontSx }}>
                <CustomMarkdown children={message.title} />
              </Typography>
            </Box>
          )}
          {message?.text && (
            <Box mb="12px" sx={messageTextStyle}>
              <CustomMarkdown children={message.text} />
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 1.5 }}>
            {buttons.flat().map((button, index) => {
              const selected =
                selectedValuesSet.has(button.data) || !!button.isChosen;
              return (
                <Chip
                  key={`${button.data}-${index}`}
                  label={button.text}
                  clickable
                  onClick={() => toggleChipSelection(button)}
                  disabled={isButtonsLocked}
                  sx={{
                    borderRadius: "999px",
                    border: "1px solid",
                    borderColor: selected
                      ? "#8B5CF6"
                      : "rgba(139, 92, 246, 0.3)",
                    backgroundColor: selected
                      ? "rgba(139, 92, 246, 0.16)"
                      : "rgba(139, 92, 246, 0.08)",
                    color: selected ? "#5B21B6" : theme.palette.buttons.textColor,
                    "& .MuiChip-label": {
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      px: 1.1,
                    },
                  }}
                />
              );
            })}
          </Box>
          {isMultichoice && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                variant="contained"
                disabled={
                  isButtonsLocked ||
                  selectedChipValues.length === 0
                }
                onClick={() => submitChipSelection(selectedChipValues)}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                }}
              >
                Send
              </Button>
            </Box>
          )}
          {!embedded && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                <ReactionAndFavorite
                  currentReaction={currentReaction}
                  markedIds={markedIds}
                  isSaved={isSaved}
                  handleRemoveTag={handleRemoveTag}
                  isTags={isTags}
                  handleReactionClick={handleReactionClick}
                  handleAddToFavorite={handleAddToFavorite}
                  reactionBg={getButtonStyles.buttonColors(theme).bg}
                />
              </Box>
              {message.date && (
                <Typography variant="caption" sx={timeStyle}>
                  <span>{format(new Date(message.date), "dd/MM/yy")}</span>
                  <span style={{ margin: "0 5px" }} />
                  <span>{format(new Date(message.date), "HH:mm")}</span>
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={rootContainerSx} position="relative">
      <Box
        sx={{
          fontSize: infoScaleSx,
          width: "100%",
        }}
      >
        {message?.title && (
          <Box
            mb="12px"
            sx={messageTextBoxSx}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: titleFontSx,
                ...(theme.palette.mode === "light"
                  ? { fontWeight: 700 }
                  : {}),
                ...(embeddedLightTextColor ? { color: embeddedLightTextColor } : {}),
              }}
            >
              <CustomMarkdown children={message.title} />
            </Typography>
          </Box>
        )}
        {message?.text && (
          <Box
            mb="12px"
            sx={(muiTheme) => ({
              ...(typeof messageTextBoxSx === "function"
                ? messageTextBoxSx(muiTheme)
                : messageTextBoxSx),
              ...(theme.palette.mode === "light"
                ? { color: "#6B7280", fontSize: "0.9375rem" }
                : {}),
            })}
          >
            <CustomMarkdown children={message.text} />
          </Box>
        )}
        <Box
          sx={
            isMobileWarning
              ? {
                  display: "flex",
                  flexDirection: isTablet ? "row" : "column",
                  gap: 2,
                  width: "100%",
                }
              : {
                  ...gridStyle,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                }
          }
        >
          {buttons.map((row, rowIndex) =>
            isMobileWarning ? (
              <React.Fragment key={rowIndex}>
                {row.map((button, buttonIndex) => {
                  const buttonColor = getResolvedButtonColor(button);
                  const buttonTextColor =
                    !button.isChosen && button.isCorrect
                      ? theme.palette.mode === "light"
                        ? "#166534"
                        : "#66EF64"
                      : undefined;

                  const buttonText = String(
                    button.text || button.data || ""
                  ).toLowerCase();
                  const isOpenButton =
                    buttonText === "open" ||
                    buttonText === "start Roleplay" ||
                    buttonText.includes("start");

                  const audioUrl =
                    message.audio?.audio_url ||
                    (button as any)?.audioUrl ||
                    (button.data as any)?.audioUrl ||
                    (message as any)?.audioUrl ||
                    (message as any)?.data?.audioUrl;

                  // Use special style for mobile warning buttons
                  const buttonStyle = isMobileWarning
                    ? mobileWarningButtonStyle(theme)
                    : getButtonStyles(
                        buttonColor,
                        buttonTextColor,
                        state === "disable",
                        message.message_type === "info"
                      );

                  if (isOpenButton && audioUrl) {
                    return (
                      <Box
                        key={`open-pair-${rowIndex}-${buttonIndex}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          alignItems: "stretch",
                          minWidth: "200px",
                          mr: 1,
                          mb: 1,
                        }}
                      >
                        <AudioButton
                          buttonText={message.audio?.button_text || "Audio"}
                          audioUrl={audioUrl}
                          onAudioStateChange={handleAudioStateChange}
                          disabled={state === "disable"}
                          transcription={resolvedTranscript.transcription}
                          transcriptionTimed={resolvedTranscript.transcriptionTimed}
                        />

                        {hasAudioPlayed && (
                          <Button
                            onClick={() =>
                              handleButtonClick(button, rowIndex, buttonIndex)
                            }
                            variant="contained"
                            disabled={isButtonDisabled(button)}
                            sx={buttonStyle}
                          >
                            <CustomMarkdown children={button.text} />
                          </Button>
                        )}
                      </Box>
                    );
                  }

                  const isMobileWarningButton =
                    button.data === "continue_mobile_warning" ||
                    button.data === "exit_mobile_warning";

                  return (
                    <Button
                      key={`${rowIndex}-${buttonIndex}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleButtonClick(button, rowIndex, buttonIndex);
                      }}
                      onMouseDown={(e) => {
                        if (isMobileWarningButton) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      variant="contained"
                      disabled={isButtonDisabled(button)}
                      sx={buttonStyle}
                    >
                      <CustomMarkdown children={button.text} />
                    </Button>
                  );
                })}
              </React.Fragment>
            ) : (
              // For regular buttons, keep the Box wrapper
              <Box key={rowIndex}>
                {row.map((button, buttonIndex) => {
                  const buttonColor = getResolvedButtonColor(button);
                  const buttonTextColor =
                    !button.isChosen && button.isCorrect
                      ? theme.palette.mode === "light"
                        ? "#166534"
                        : "#66EF64"
                      : undefined;

                  const buttonText = String(
                    button.text || button.data || ""
                  ).toLowerCase();
                  const isOpenButton =
                    buttonText === "open" ||
                    buttonText === "start Roleplay" ||
                    buttonText.includes("start");

                  const audioUrl =
                    message.audio?.audio_url ||
                    (button as any)?.audioUrl ||
                    (button.data as any)?.audioUrl ||
                    (message as any)?.audioUrl ||
                    (message as any)?.data?.audioUrl;

                  const buttonStyle = getButtonStyles(
                    buttonColor,
                    buttonTextColor,
                    state === "disable",
                    message.message_type === "info"
                  );

                  if (isOpenButton && audioUrl) {
                    return (
                      <Box
                        key={`open-pair-${rowIndex}-${buttonIndex}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          alignItems: "stretch",
                          minWidth: "200px",
                          mr: 1,
                          mb: 1,
                        }}
                      >
                        <AudioButton
                          buttonText={message.audio?.button_text || "Audio"}
                          audioUrl={audioUrl}
                          onAudioStateChange={handleAudioStateChange}
                          disabled={state === "disable"}
                          transcription={resolvedTranscript.transcription}
                          transcriptionTimed={resolvedTranscript.transcriptionTimed}
                        />

                        {hasAudioPlayed && (
                          <Button
                            onClick={() =>
                              handleButtonClick(button, rowIndex, buttonIndex)
                            }
                            variant="contained"
                            disabled={isButtonDisabled(button)}
                            sx={buttonStyle}
                          >
                            <CustomMarkdown children={button.text} />
                          </Button>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <Button
                      key={buttonIndex}
                      onClick={() =>
                        handleButtonClick(button, rowIndex, buttonIndex)
                      }
                      variant="contained"
                      disabled={isButtonDisabled(button)}
                      sx={buttonStyle}
                    >
                      <CustomMarkdown children={button.text} />
                    </Button>
                  );
                })}
              </Box>
            )
          )}
        </Box>
        {!embedded && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              <ReactionAndFavorite
                currentReaction={currentReaction}
                markedIds={markedIds}
                isSaved={isSaved}
                handleRemoveTag={handleRemoveTag}
                isTags={isTags}
                handleReactionClick={handleReactionClick}
                handleAddToFavorite={handleAddToFavorite}
                reactionBg={getButtonStyles.buttonColors(theme).bg}
              />
            </Box>
            {message.date && (
              <Typography variant="caption" sx={timeStyle}>
              <span>{format(new Date(message.date), "dd/MM/yy")}</span>
              <span style={{ margin: "0 5px" }} />
              <span>{format(new Date(message.date), "HH:mm")}</span>
            </Typography>
          )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MessageButtons;
